import { clerkClient } from "@/App";
import { api } from "./api";
import { useQuery } from "@tanstack/react-query";

// Types that match the backend types
export interface GeofenceViolation {
  violationId: string;
  sensorId: string;
  detectionId: string;
  geofenceId: string;
  userId: string;
  organizationId: string;
  droneIdentifier: string;
  violationTime: string;
  createdAt: string;
}

export interface DroneSummary {
  droneIdentifier: string;
  violationCount: number;
  firstViolation: string;
  lastViolation: string;
  geofenceIds: string[];
}

export interface ViolationsSummary {
  totalViolations: number;
  uniqueDrones: number;
  uniqueGeofences: number;
  uniqueSensors: number;
  earliestViolation: string;
  latestViolation: string;
  hasMoreViolations: boolean;
  timeRangeStart: string;
  timeRangeEnd: string;
}

export interface GroupedViolationsResponse {
  drones: DroneSummary[];
  summary: ViolationsSummary;
  violations: GeofenceViolation[];
}

interface ViolationsFilters {
  startTime?: string;
  endTime?: string;
  drone_id?: string;
  geofence_id?: string;
  limit?: number;
  offset?: number;
}


// Fetch grouped violations summary
export const fetchGroupedViolationsQuery = async (filters?: ViolationsFilters) => {
  const authToken = await clerkClient.session?.getToken();
  
  // Build query parameters
  const params = new URLSearchParams();
  if (filters?.startTime) params.append("startTime", filters.startTime);
  if (filters?.endTime) params.append("endTime", filters.endTime);
  if (filters?.drone_id) params.append("drone_id", filters.drone_id);
  if (filters?.geofence_id) params.append("geofence_id", filters.geofence_id);
  if (filters?.limit) params.append("limit", filters.limit.toString());
  if (filters?.offset) params.append("offset", filters.offset.toString());
  
  const queryString = params.toString() ? `?${params.toString()}` : "";
  
  const resp = await api.get(`/v0/geofence-violations/grouped${queryString}`, {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  });

  return resp.data as GroupedViolationsResponse;
};

// React Query hook for fetching grouped violations
export const useGroupedViolationsQuery = (filters?: ViolationsFilters) => {
  return useQuery({
    queryKey: ["groupedViolations", filters],
    queryFn: () => fetchGroupedViolationsQuery(filters),
  });
};

// Fetch violations for a specific geofence
export const fetchGeofenceViolationsQuery = async (
  geofenceId: string,
  filters?: Omit<ViolationsFilters, 'geofence_id'>
) => {
  const authToken = await clerkClient.session?.getToken();
  
  // Build query parameters
  const params = new URLSearchParams();
  if (filters?.startTime) params.append("startTime", filters.startTime);
  if (filters?.endTime) params.append("endTime", filters.endTime);
  if (filters?.drone_id) params.append("drone_id", filters.drone_id);
  if (filters?.limit) params.append("limit", filters.limit.toString());
  if (filters?.offset) params.append("offset", filters.offset.toString());
  
  const queryString = params.toString() ? `?${params.toString()}` : "";
  
  const resp = await api.get(`/v0/geofences/${geofenceId}/violations${queryString}`, {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  });

  return resp.data as GroupedViolationsResponse;
};

// React Query hook for fetching violations for a specific geofence
export const useGeofenceViolationsQuery = (
  geofenceId: string,
  filters?: Omit<ViolationsFilters, 'geofence_id'>
) => {
  return useQuery({
    queryKey: ["geofenceViolations", geofenceId, filters],
    queryFn: () => fetchGeofenceViolationsQuery(geofenceId, filters),
    enabled: !!geofenceId,
  });
};
